/**
 * Uploads a file to the API
 * @param file - The file to upload
 * @param accessToken - The access token for authentication
 * @returns Promise that resolves to the file ID or null if upload fails
 */
export async function uploadFile(
  file: File,
  accessToken: string
): Promise<number | null> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
      console.warn("REACT_APP_API_BASE_URL is not defined");
      return null;
    }

    const endpoint = `${API_BASE_URL}/api/files/files/upload/`;

    // Create FormData to send the file
    const formData = new FormData();
    formData.append("file", file);

    const { authenticatedFetch } = await import("../features/auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    const responseBody = await response.json().catch(() => null);

    if (!response.ok || !responseBody?.data?.file_id) {
      console.error("File upload failed:", responseBody);
      return null;
    }

    return responseBody.data.file_id as number;
  } catch (error) {
    console.error("Failed to upload file:", error);
    return null;
  }
}

/**
 * Creates a file input element and triggers file selection
 * @param accept - File types to accept (e.g., "image/*")
 * @returns Promise that resolves to the selected file or null if cancelled
 */
export function selectFile(accept: string = "image/*"): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.style.display = "none";

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0] || null;
      document.body.removeChild(input);
      resolve(file);
    };

    input.oncancel = () => {
      document.body.removeChild(input);
      resolve(null);
    };

    document.body.appendChild(input);
    input.click();
  });
}

